<script lang="ts">
  // A chip in the shell's community header while the ACTIVE community is still on its
  // free trial: a live d/h/m/s countdown to that community's trial end + an Upgrade
  // button that starts a per-community Stripe Checkout. Renders nothing unless the
  // selected community is mid-trial with a known end date. Trial + billing are
  // per-skool now, so this follows the community store's selection, not the account.
  //
  // It was a full-width dark-blue strip above the panel; it moved into the header so
  // the two community-scoped notices sit with the community they are about. Tinted
  // rather than filled: at strip width a solid blue bar read as the page's headline,
  // and at chip width beside a name it would read as the primary action.
  import { activeCommunity } from '../community.svelte';
  import { ApiError } from '../admin';
  import { startCheckout } from '@api';

  // The community the panel is currently scoped to (sidebar selector).
  const active = $derived(activeCommunity());

  // Tick every second so the seconds field stays live while the panel is open.
  let nowMs = $state(Date.now());
  $effect(() => {
    const id = setInterval(() => (nowMs = Date.now()), 1000);
    return () => clearInterval(id);
  });

  const show = $derived(active?.plan_status === 'trial' && !!active.trial_ends_at);
  const endMs = $derived(active?.trial_ends_at ? Date.parse(active.trial_ends_at) : 0);
  const remaining = $derived(Math.max(0, endMs - nowMs));

  const parts = $derived.by(() => {
    let s = Math.floor(remaining / 1000);
    const days = Math.floor(s / 86400);
    s -= days * 86400;
    const hours = Math.floor(s / 3600);
    s -= hours * 3600;
    const minutes = Math.floor(s / 60);
    const seconds = s - minutes * 60;
    return { days, hours, minutes, seconds };
  });
  const pad = (n: number): string => String(n).padStart(2, '0');

  let upgrading = $state(false);
  let error = $state<string | null>(null);

  async function upgrade(): Promise<void> {
    if (!active || upgrading) return;
    upgrading = true;
    error = null;
    try {
      // `POST /v1/communities/{id}/billing/checkout` — the community moved from the
      // request body into the path; the Stripe call behind it is the same
      // `stripe_gateway.checkout_url(session, user, skool, origin)`, so the price and
      // the community being subscribed are unchanged. The body carries `community`
      // and `plan_status` too; we redirect, so `url` is all this caller reads.
      const { url } = await startCheckout(active.id);
      window.location.href = url; // hand off to Stripe's hosted checkout
    } catch (err: unknown) {
      error =
        err instanceof ApiError && err.status === 503
          ? "Billing isn't configured yet."
          : err instanceof Error
            ? err.message
            : 'Could not start checkout.';
      upgrading = false; // on success we've already navigated away
    }
  }
</script>

{#if show}
  <div
    class="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[0.7rem] text-primary"
    role="status"
    aria-live="off"
  >
    <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" />
    </svg>
    <!-- "Free trial ends in" is the header's to imply: the chip sits beside the
         community it belongs to, so only the clock and the action need saying. The
         full sentence stays in the title for anyone who needs it spelled out. -->
    <span class="font-semibold tabular-nums" title="Free trial ends in {parts.days}d {pad(parts.hours)}h {pad(parts.minutes)}m">
      <span class="hidden sm:inline">Trial </span>{parts.days}d {pad(parts.hours)}:{pad(parts.minutes)}:{pad(parts.seconds)}
    </span>
    <button
      onclick={upgrade}
      disabled={upgrading}
      class="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[0.7rem] font-semibold text-primary-content transition-opacity hover:opacity-90 disabled:opacity-60"
    >
      {#if upgrading}<span class="loading loading-spinner loading-xs"></span>{/if}
      Upgrade
    </button>
    {#if error}
      <span class="max-w-[14rem] truncate text-[0.7rem] text-error" title={error}>{error}</span>
    {/if}
  </div>
{/if}
